import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Box,
  CircularProgress,
} from '@mui/material';
import { Document } from '../../types/Document';
import { Category } from '../../types/Category';
import {
  DocumentReadAccessGrant,
  mergeFromReadAccessGrantValue,
  parseMergeFromReadAccessGrantValue,
  readAccessGrantMergeLabel,
} from '../../types/DocumentReadAccess';
import { MergeMissingResolve, MergeTypeMismatchResolve } from '../../types/MergeTypes';
import { SelectiveMergeBaseline } from '../../types/DocumentCompare';
import { styled } from '@mui/material/styles';
import { DropdownFieldTooltip } from '../DropdownFieldTooltip';
import { useHelpContent } from '../../utils/HelpUtils';
import { FEATURE_FLAG_KEYS, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import api from '../../services/api';

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    minWidth: '480px',
    maxWidth: '520px',
  },
});

const StyledFormControl = styled(FormControl)({
  marginTop: '16px',
  width: '100%',
});

interface MergeDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  onMerge: (
    mergeIntoDocumentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    newName: string,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => void;
  onCompare: (
    mergeIntoDocumentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    leftLabel: string,
    rightLabel: string,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => void;
  onSelectiveMerge: (
    mergeIntoDocumentId: string,
    mergeFromDocumentId: string,
    mergeFromReadAccessGrantId: number | null,
    leftLabel: string,
    rightLabel: string,
    newName: string,
    baseline: SelectiveMergeBaseline,
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => void;
  documents: Document[];
  /** Active view-only grants from other users (eligible merge sources). */
  sharedReadAccessGrants?: DocumentReadAccessGrant[];
  /** When set (list selection or context menu), this document is the merge target. */
  currentDocument: Document | undefined;
  /** Pre-selected right document id or view-only grant token for compare mode. */
  initialMergeFromId?: string;
  /** Compare-only: hide merge actions and “new document”; Compare is primary. */
  mode?: 'merge' | 'compare';
}

const MergeDocumentDialog: React.FC<MergeDocumentDialogProps> = ({
  open,
  onClose,
  onMerge,
  onCompare,
  onSelectiveMerge,
  documents,
  sharedReadAccessGrants = [],
  currentDocument,
  initialMergeFromId,
  mode = 'merge',
}) => {
  const isCompareMode = mode === 'compare';
  const [mergeIntoDocumentId, setMergeIntoDocumentId] = useState('');
  const [mergeFromDocumentId, setMergeFromDocumentId] = useState('');
  const [newName, setNewName] = useState('');
  const [resolveMissing, setResolveMissing] = useState<MergeMissingResolve>(MergeMissingResolve.COPY);
  const [resolveTypeMismatch, setResolveTypeMismatch] = useState<MergeTypeMismatchResolve>(
    MergeTypeMismatchResolve.KEEP,
  );
  const [nameError, setNameError] = useState('');
  const [createNewDocument, setCreateNewDocument] = useState(false);
  const [createSelectiveMergeDocument, setCreateSelectiveMergeDocument] = useState(false);
  const [selectiveMergeBaseline, setSelectiveMergeBaseline] =
    useState<SelectiveMergeBaseline>('intersection');
  const { getTooltipTextForState } = useHelpContent();
  const { isEnabled } = useFeatureFlags();
  const documentMergeCompareEnabled = isEnabled(FEATURE_FLAG_KEYS.DOCUMENT_MERGE_COMPARE);
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);

  const needsMergeIntoPicker = !currentDocument;

  const effectiveMergeInto = useMemo(() => {
    if (currentDocument) {
      return currentDocument;
    }
    const id = mergeIntoDocumentId.trim();
    if (!id) {
      return undefined;
    }
    return documents.find((doc) => doc.id === id || doc.name === id);
  }, [currentDocument, mergeIntoDocumentId, documents]);

  const mergeFromReadAccessGrantId = useMemo(
    () => parseMergeFromReadAccessGrantValue(mergeFromDocumentId.trim()),
    [mergeFromDocumentId],
  );

  const availableMergeIntoDocuments = useMemo(() => {
    if (mergeFromReadAccessGrantId != null) {
      return documents;
    }
    return documents.filter(
      (doc) => doc.id !== mergeFromDocumentId && doc.name !== mergeFromDocumentId,
    );
  }, [documents, mergeFromDocumentId, mergeFromReadAccessGrantId]);

  const availableMergeFromDocuments = useMemo(
    () =>
      documents.filter(
        (doc) =>
          doc.id !== effectiveMergeInto?.id && doc.name !== effectiveMergeInto?.name,
      ),
    [documents, effectiveMergeInto],
  );

  const availableSharedMergeFromGrants = useMemo(
    () => sharedReadAccessGrants,
    [sharedReadAccessGrants],
  );

  const isMergeFromSelected = (): boolean => {
    const id = mergeFromDocumentId.trim();
    if (!id) return false;
    if (mergeFromReadAccessGrantId != null) {
      return availableSharedMergeFromGrants.some((g) => g.id === mergeFromReadAccessGrantId);
    }
    return availableMergeFromDocuments.some((doc) => doc.id === id || doc.name === id);
  };

  const isMergeIntoSelected = (): boolean => Boolean(effectiveMergeInto?.id);

  useEffect(() => {
    if (!open) return;
    setMergeIntoDocumentId('');
    setMergeFromDocumentId(initialMergeFromId ?? '');
    setNewName('');
    setResolveMissing(MergeMissingResolve.COPY);
    setResolveTypeMismatch(MergeTypeMismatchResolve.KEEP);
    setNameError('');
    setCreateNewDocument(false);
    setCreateSelectiveMergeDocument(false);
    setSelectiveMergeBaseline('intersection');
    setSubCategories([]);
  }, [open, initialMergeFromId]);

  const needsNewDocumentName = createNewDocument || createSelectiveMergeDocument;

  const loadSubCategories = async (mergeFrom: string): Promise<string[]> => {
    setLoadingSubCategories(true);
    let loadedSubCategories: string[] = [];

    try {
      if (effectiveMergeInto?.name && effectiveMergeInto.categoryId) {
        await api.post('/documents/open', null, {
          params: { documentId: effectiveMergeInto.name },
        });
        const res = await api.get('/categories/subcategories', {
          params: { categoryId: effectiveMergeInto.categoryId },
        });
        loadedSubCategories = res.data.map((c: Category) => c.name);
      }

      if (mergeFrom !== '') {
        const grantId = parseMergeFromReadAccessGrantValue(mergeFrom);
        if (grantId != null) {
          const openRes = await api.post<{ categoryId: string }>(
            `/documents/read-access/${grantId}/open`,
          );
          if (openRes.data?.categoryId) {
            const mergeRes = await api.get('/categories/subcategories', {
              params: { categoryId: openRes.data.categoryId },
            });
            loadedSubCategories.push(...mergeRes.data.map((c: Category) => c.name));
          }
        } else {
          const mergeFromDoc = documents.find((doc) => doc.id === mergeFrom);
          if (mergeFromDoc?.name && mergeFromDoc.categoryId) {
            await api.post('/documents/open', null, {
              params: { documentId: mergeFromDoc.name },
            });
            const mergeRes = await api.get('/categories/subcategories', {
              params: { categoryId: mergeFromDoc.categoryId },
            });
            loadedSubCategories.push(...mergeRes.data.map((c: Category) => c.name));
          }
        }
      }

      setSubCategories(loadedSubCategories);
      return loadedSubCategories;
    } catch (err) {
      console.error('Failed to load merge subcategories', err);
      setSubCategories([]);
      return [];
    } finally {
      setLoadingSubCategories(false);
    }
  };

  useEffect(() => {
    if (needsNewDocumentName && effectiveMergeInto && mergeFromDocumentId) {
      loadSubCategories(resolveMissing === MergeMissingResolve.COPY ? mergeFromDocumentId : '');
    } else {
      setSubCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when merge pair changes
  }, [needsNewDocumentName, effectiveMergeInto?.id, mergeFromDocumentId, resolveMissing]);

  useEffect(() => {
    if (!open || !needsNewDocumentName) return;
    const trimmed = newName.trim();
    if (!trimmed) {
      setNameError('');
      return;
    }
    setNameError(validateNewDocumentName(trimmed));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-check when library list changes
  }, [open, needsNewDocumentName, documents, newName, subCategories]);

  const normalizeName = (s: string) =>
    (s || '')
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const documentDisplayName = (doc: Document) => (doc.label || doc.name || '').trim();

  const mergeTargetDisplayNorm = normalizeName(
    effectiveMergeInto?.label || effectiveMergeInto?.name || '',
  );

  /** Merge mode: same name as merge-into → in-place merge instead of a new library row. */
  const isSameNameAsMergeTarget = (trimmedName: string): boolean =>
    !!mergeTargetDisplayNorm && normalizeName(trimmedName) === mergeTargetDisplayNorm;

  /** True when another library document already uses this name (case-insensitive, trimmed). */
  const isExistingDocumentName = (trimmedName: string) => {
    const nn = normalizeName(trimmedName);
    if (nn === mergeTargetDisplayNorm) return false;
    return documents.some((doc) => normalizeName(documentDisplayName(doc)) === nn);
  };

  const validateNewDocumentName = (trimmedName: string, subs: string[] = subCategories): string => {
    if (!trimmedName) return 'Document name cannot be empty';
    if (
      isCompareMode &&
      createSelectiveMergeDocument &&
      isSameNameAsMergeTarget(trimmedName)
    ) {
      return 'New document name must differ from the Compare document';
    }
    if (isExistingDocumentName(trimmedName)) return 'A document with this name already exists';
    if (subs.map(normalizeName).includes(normalizeName(trimmedName))) {
      return 'Document contains a sub category with this name';
    }
    return '';
  };

  const handleNameChange = (value: string) => {
    setNewName(value);
    setNameError(validateNewDocumentName(value.trim()));
  };

  const handleResolveMissingChange = async (value: MergeMissingResolve) => {
    setResolveMissing(value);

    const loadedSubCategories = await loadSubCategories(
      value === MergeMissingResolve.COPY ? mergeFromDocumentId : '',
    );

    setNameError(validateNewDocumentName(newName.trim(), loadedSubCategories));
  };

  const handleMergeIntoChanged = (value: string) => {
    setMergeIntoDocumentId(value);
    if (value === mergeFromDocumentId) {
      setMergeFromDocumentId('');
    }
    setNameError(validateNewDocumentName(newName.trim()));
  };

  const handleMergeFromChanged = async (value: string) => {
    setMergeFromDocumentId(value);

    const loadedSubCategories = await loadSubCategories(
      resolveMissing === MergeMissingResolve.COPY ? value : '',
    );

    setNameError(validateNewDocumentName(newName.trim(), loadedSubCategories));
  };

  const handleCreateNewDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCreateNewDocument(event.target.checked);
    if (!event.target.checked) {
      setNewName('');
      setNameError('');
    }
  };

  const handleCreateSelectiveMergeDocumentChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setCreateSelectiveMergeDocument(event.target.checked);
    if (!event.target.checked) {
      setNewName('');
      setNameError('');
    }
  };

  const resolveRightLabel = (): string => {
    const grantId = mergeFromReadAccessGrantId;
    if (grantId != null) {
      const grant = availableSharedMergeFromGrants.find((g) => g.id === grantId);
      return grant ? readAccessGrantMergeLabel(grant) : '';
    }
    const doc = availableMergeFromDocuments.find(
      (d) => d.id === mergeFromDocumentId || d.name === mergeFromDocumentId,
    );
    return doc?.label || doc?.name || mergeFromDocumentId;
  };

  const resolveLeftLabel = (): string =>
    effectiveMergeInto?.label || effectiveMergeInto?.name || '';

  const handleSelectiveMerge = () => {
    if (!effectiveMergeInto?.id || !isMergeFromSelected()) return;
    const trimmed = newName.trim();
    const err = validateNewDocumentName(trimmed);
    if (err) {
      setNameError(err);
      return;
    }
    const grantId = mergeFromReadAccessGrantId;
    const fromDocId = grantId != null ? '' : mergeFromDocumentId;
    onSelectiveMerge(
      effectiveMergeInto.id,
      fromDocId,
      grantId,
      resolveLeftLabel(),
      resolveRightLabel(),
      trimmed,
      isCompareMode ? selectiveMergeBaseline : 'left',
      isCompareMode ? MergeMissingResolve.COPY : resolveMissing,
      isCompareMode ? MergeTypeMismatchResolve.KEEP : resolveTypeMismatch,
    );
    onClose();
  };

  const handleMerge = () => {
    if (!isMergeIntoSelected() || !isMergeFromSelected() || !effectiveMergeInto?.id) return;
    const grantId = mergeFromReadAccessGrantId;
    const fromDocId = grantId != null ? '' : mergeFromDocumentId;
    if (createNewDocument) {
      const trimmed = newName.trim();
      if (!trimmed) return;
      if (isSameNameAsMergeTarget(trimmed)) {
        onMerge(
          effectiveMergeInto.id,
          fromDocId,
          grantId,
          '',
          resolveMissing,
          resolveTypeMismatch,
        );
        onClose();
        return;
      }
      handleSelectiveMerge();
      return;
    }
    onMerge(
      effectiveMergeInto.id,
      fromDocId,
      grantId,
      '',
      resolveMissing,
      resolveTypeMismatch,
    );
    onClose();
  };

  const isSelectiveMergeDisabled = () => {
    if (!isMergeIntoSelected() || !isMergeFromSelected()) return true;
    const trimmed = newName.trim();
    if (!trimmed) return true;
    return !!validateNewDocumentName(trimmed);
  };

  const isMergeButtonDisabled = () => {
    if (!isMergeIntoSelected() || !isMergeFromSelected()) return true;
    if (createNewDocument) {
      const trimmed = newName.trim();
      if (!trimmed) return true;
      if (isSameNameAsMergeTarget(trimmed)) return false;
      return !!validateNewDocumentName(trimmed);
    }
    return false;
  };

  const mergePrimaryButtonLabel = (): string => {
    if (!createNewDocument) return 'Merge';
    const trimmed = newName.trim();
    if (trimmed && isSameNameAsMergeTarget(trimmed)) return 'Merge';
    return 'Build new document';
  };

  const isCompareDisabled = () => !isMergeIntoSelected() || !isMergeFromSelected();

  const handleCompare = () => {
    if (!effectiveMergeInto?.id || isCompareDisabled()) return;
    const grantId = mergeFromReadAccessGrantId;
    const fromDocId = grantId != null ? '' : mergeFromDocumentId;
    onCompare(
      effectiveMergeInto.id,
      fromDocId,
      grantId,
      resolveLeftLabel(),
      resolveRightLabel(),
      MergeMissingResolve.COPY,
      MergeTypeMismatchResolve.KEEP,
    );
    onClose();
  };

  const renderSelectiveMergeBaselineOptions = () => {
    const leftLabel = resolveLeftLabel();
    const rightLabel = resolveRightLabel();
    return (
      <FormControl component="fieldset" sx={{ mt: 1 }}>
        <FormLabel component="legend">Starting content in the new document</FormLabel>
        <RadioGroup
          value={selectiveMergeBaseline}
          onChange={(e) =>
            setSelectiveMergeBaseline(e.target.value as SelectiveMergeBaseline)
          }
        >
          <FormControlLabel
            value="intersection"
            control={<Radio size="small" />}
            label="Matched on both sides only (recommended)"
          />
          <FormControlLabel
            value="left"
            control={<Radio size="small" />}
            label={`All of ${leftLabel} — add from ${rightLabel}`}
          />
          <FormControlLabel
            value="right"
            control={<Radio size="small" />}
            label={`All of ${rightLabel} — add from ${leftLabel}`}
          />
        </RadioGroup>
      </FormControl>
    );
  };

  const renderMergeFromSelectValue = (selected: string) => {
    if (!selected) {
      return <Typography color="text.secondary">Select a document…</Typography>;
    }
    const grantId = parseMergeFromReadAccessGrantValue(selected);
    if (grantId != null) {
      const grant = availableSharedMergeFromGrants.find((g) => g.id === grantId);
      return grant ? readAccessGrantMergeLabel(grant) : '';
    }
    const doc = availableMergeFromDocuments.find((d) => d.id === selected || d.name === selected);
    return doc?.label || doc?.name || '';
  };

  const renderDocumentSelectValue = (selected: string, options: Document[]) => {
    if (!selected) {
      return <Typography color="text.secondary">Select a document…</Typography>;
    }
    const doc = options.find((d) => d.id === selected || d.name === selected);
    return doc?.label || doc?.name || '';
  };

  const leftSideLabel = isCompareMode ? 'Compare' : 'Merge into';
  const rightSideLabel = isCompareMode ? 'Compare with' : 'Merge From';

  return (
    <StyledDialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
    >
      <DialogTitle>{isCompareMode ? 'Compare Documents' : 'Merge Document'}</DialogTitle>
      <DialogContent>
        {needsMergeIntoPicker ? (
          <Tooltip
            title={getTooltipTextForState(
              isCompareMode ? 'document-merge-compare-dialog' : 'merge-document',
              true,
            )}
            placement="top"
          >
            <StyledFormControl>
              <InputLabel id="merge-into-label" shrink>
                {leftSideLabel}
              </InputLabel>
              <Select
                labelId="merge-into-label"
                value={mergeIntoDocumentId}
                onChange={(e) => handleMergeIntoChanged(e.target.value)}
                label={leftSideLabel}
                displayEmpty
                renderValue={(selected) =>
                  renderDocumentSelectValue(selected, availableMergeIntoDocuments)
                }
              >
                <MenuItem value="" disabled>
                  <em>Select a document…</em>
                </MenuItem>
                {availableMergeIntoDocuments.map((doc) => (
                  <MenuItem key={doc.id} value={doc.id}>
                    {doc.label}
                  </MenuItem>
                ))}
              </Select>
            </StyledFormControl>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {leftSideLabel}: <strong>{currentDocument?.label || currentDocument?.name}</strong>
          </Typography>
        )}

        <Tooltip
          title={getTooltipTextForState(
            isCompareMode ? 'document-merge-compare-with' : 'merge-document-from',
            true,
          )}
          placement="top"
        >
          <StyledFormControl>
            <InputLabel id="merge-from-label" shrink>
              {rightSideLabel}
            </InputLabel>
            <Select
              labelId="merge-from-label"
              value={mergeFromDocumentId}
              onChange={(e) => handleMergeFromChanged(e.target.value)}
              label={rightSideLabel}
              displayEmpty
              disabled={needsMergeIntoPicker && !mergeIntoDocumentId}
              renderValue={(selected) => renderMergeFromSelectValue(selected)}
            >
              <MenuItem value="" disabled>
                <em>Select a document…</em>
              </MenuItem>
              {availableMergeFromDocuments.length > 0 && (
                <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem' }}>
                  Your documents
                </MenuItem>
              )}
              {availableMergeFromDocuments.map((doc) => (
                <MenuItem key={doc.id} value={doc.id}>
                  {doc.label}
                </MenuItem>
              ))}
              {availableSharedMergeFromGrants.length > 0 && (
                <MenuItem disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem' }}>
                  View only shared with me
                </MenuItem>
              )}
              {availableSharedMergeFromGrants.map((grant) => (
                <MenuItem key={`grant-${grant.id}`} value={mergeFromReadAccessGrantValue(grant.id)}>
                  {readAccessGrantMergeLabel(grant)}
                </MenuItem>
              ))}
            </Select>
          </StyledFormControl>
        </Tooltip>

        {!isCompareMode && documentMergeCompareEnabled ? (
          <>
            <Tooltip title={getTooltipTextForState('merge-document-new', true)} placement="top">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createNewDocument}
                    onChange={handleCreateNewDocumentChange}
                    disabled={!isMergeIntoSelected()}
                  />
                }
                label="Merge into a new document"
                sx={{ mt: 2 }}
              />
            </Tooltip>

            {createNewDocument &&
              (loadingSubCategories ? (
                <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading groups...</Typography>
                </Box>
              ) : (
                <>
                  <Tooltip title={getTooltipTextForState('merge-document-name', true)} placement="top">
                    <TextField
                      fullWidth
                      label="New Document Name"
                      value={newName}
                      onChange={(e) => handleNameChange(e.target.value)}
                      error={!!nameError}
                      helperText={nameError}
                      margin="normal"
                    />
                  </Tooltip>
                </>
              ))}
          </>
        ) : documentMergeCompareEnabled ? (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={createSelectiveMergeDocument}
                  onChange={handleCreateSelectiveMergeDocumentChange}
                  disabled={!isMergeIntoSelected()}
                />
              }
              label="Into a new document"
              sx={{ mt: 2 }}
            />
            {createSelectiveMergeDocument &&
              (loadingSubCategories ? (
                <Box sx={{ my: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading groups...</Typography>
                </Box>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="New Document Name"
                    value={newName}
                    onChange={(e) => handleNameChange(e.target.value)}
                    error={!!nameError}
                    helperText={nameError}
                    margin="normal"
                  />
                  {renderSelectiveMergeBaselineOptions()}
                </>
              ))}
          </>
        ) : null}

        {!isCompareMode ? (
          <Box sx={{ width: '100%' }}>
            <DropdownFieldTooltip title={getTooltipTextForState('merge-document-missing-resolution', true)}>
              <StyledFormControl>
                <InputLabel id="merge-missing-resolve-label" shrink>
                  Missing content
                </InputLabel>
                <Select
                  labelId="merge-missing-resolve-label"
                  value={resolveMissing}
                  onChange={(e) => handleResolveMissingChange(e.target.value as MergeMissingResolve)}
                  label="Missing content"
                  fullWidth
                >
                  <MenuItem value={MergeMissingResolve.COPY}>Copy from source</MenuItem>
                  <MenuItem value={MergeMissingResolve.SKIP}>Skip</MenuItem>
                  <MenuItem value={MergeMissingResolve.ABORT}>Abort merge</MenuItem>
                </Select>
              </StyledFormControl>
            </DropdownFieldTooltip>

            <DropdownFieldTooltip title={getTooltipTextForState('merge-document-type-resolution', true)}>
              <StyledFormControl>
                <InputLabel id="merge-type-resolve-label" shrink>
                  Type conflict
                </InputLabel>
                <Select
                  labelId="merge-type-resolve-label"
                  value={resolveTypeMismatch}
                  onChange={(e) => setResolveTypeMismatch(e.target.value as MergeTypeMismatchResolve)}
                  label="Type conflict"
                  fullWidth
                >
                  <MenuItem value={MergeTypeMismatchResolve.KEEP}>Keep target</MenuItem>
                  <MenuItem value={MergeTypeMismatchResolve.SKIP}>Skip</MenuItem>
                  <MenuItem value={MergeTypeMismatchResolve.ABORT}>Abort merge</MenuItem>
                </Select>
              </StyledFormControl>
            </DropdownFieldTooltip>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Tooltip title={getTooltipTextForState('cancel', true)} placement="top">
          <Button onClick={onClose}>Cancel</Button>
        </Tooltip>
        {isCompareMode ? (
          createSelectiveMergeDocument ? (
            <Tooltip
              title={getTooltipTextForState(
                isSelectiveMergeDisabled()
                  ? 'document-merge-compare-disabled'
                  : 'document-merge-compare-dialog',
                true,
              )}
              placement="top"
            >
              <span>
                <Button
                  onClick={handleSelectiveMerge}
                  variant="contained"
                  disabled={isSelectiveMergeDisabled()}
                >
                  Build new document
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Tooltip
              title={getTooltipTextForState(
                isCompareDisabled() ? 'document-merge-compare-disabled' : 'document-merge-compare-dialog',
                true,
              )}
              placement="top"
            >
              <span>
                <Button
                  onClick={handleCompare}
                  variant="contained"
                  disabled={isCompareDisabled()}
                >
                  Compare
                </Button>
              </span>
            </Tooltip>
          )
        ) : (
          <Tooltip
            title={getTooltipTextForState(
              isMergeButtonDisabled() ? 'merge-document-merge-disabled' : 'merge-document-merge',
              true,
            )}
            placement="top"
          >
            <span>
              <Button onClick={handleMerge} variant="contained" disabled={isMergeButtonDisabled()}>
                {mergePrimaryButtonLabel()}
              </Button>
            </span>
          </Tooltip>
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default MergeDocumentDialog;
